import React, {
    useRef,
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle
} from "react";
import ReactECharts from "echarts-for-react";
import {
    Box,
    IconButton,
    MenuItem,
    Popover,
    TextField,
    Tooltip,
    Typography, useTheme
} from "@mui/material";
import { msg, Trans } from "@lingui/macro";
import { Download, PictureAsPdf } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useLingui } from "@lingui/react";
import useWindowSize from "../../../hooks/useWindowSize";
import jsPDF from "jspdf";
import SvgCustomIcon from "../../icons/SvgCustomIcon";
import {chartPalette} from "./Dashboard";

const EChart = forwardRef(({ areasGeoJson, chart, index, handleCharts, width }, ref) => {
    const windowSize = useWindowSize();
    const { _ } = useLingui();

    const [anchorEl, setAnchorEl] = useState(null);
    const [locationsList, setLocationsList] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const theme = useTheme();

    const chartRef = useRef();

    // Espone metodi al parent
    useImperativeHandle(ref, () => ({
        getImage: () => {
            const instance = chartRef.current?.getEchartsInstance();
            if (!instance) return null;
            return instance.getDataURL({
                type: "png",
                pixelRatio: 2,
                backgroundColor: "#fff"
            });
        }
    }));

    useEffect(() => {
        if (chart.type === "map") {
            const l = chart.data
                .map(el => el.name)
                .filter(Boolean);
            setLocationsList(l);
            setSelectedLocation(l[0]);
        }
    }, [chart]);

    useEffect(() => {
        if (selectedLocation) {
            centerOnRegion(selectedLocation);
        }
    }, [selectedLocation]);

    const getRegionCenterByName = regionName => {
        const regionNameRefactor = regionName === "Chhuk" ? "Chuuk" : regionName;
        const feature = areasGeoJson?.features.find(
            f => f.properties.Name === regionNameRefactor
        );
        if (!feature) return null;
        return getFeatureCenter(feature);
    };

    const getFeatureCenter = feature => {
        const coordinates = feature.geometry.coordinates;
        const polygon =
            feature.geometry.type === "MultiPolygon"
                ? coordinates[0][0]
                : coordinates[0];
        let x = 0,
            y = 0,
            len = polygon.length;
        polygon.forEach(coord => {
            x += coord[0];
            y += coord[1];
        });
        return [x / len, y / len];
    };

    const centerOnRegion = regionName => {
        const center = getRegionCenterByName(regionName);
        if (!center) return;
        const instance = chartRef.current?.getEchartsInstance();
        instance?.setOption({
            series: [{ center, zoom: 12, roam: true }]
        });
    };

    const getChartOption = (chart) => {
        const labels = chart.data.map(el => el.label);
        const values = chart.data.map(el => el.value);

        const isDark = theme.palette.mode === "dark";
        const textColor = isDark ? "#fff" : "#333";
        const gridColor = isDark ? "#888" : "#ccc";

        const getRandomPaletteFromChartPalette = (n) => {
            const palette = [...chartPalette];
            const result = [];
            for (let i = 0; i < n; i++) {
                if (palette.length === 0) break;
                const idx = Math.floor(Math.random() * palette.length);
                result.push(palette[idx]);
                palette.splice(idx, 1);
            }
            return result;
        };

        switch (chart.type) {
            case "bar":
                return {
                    color: chartPalette[0],
                    xAxis: {
                        type: "category",
                        data: labels,
                        name: chart.xLabel,
                        axisLine: { lineStyle: { color: gridColor } },
                        axisLabel: { color: textColor }
                    },
                    yAxis: {
                        type: "value",
                        name: chart.yLabel,
                        axisLine: { lineStyle: { color: gridColor } },
                        axisLabel: { color: textColor }
                    },
                    series: [
                        {
                            type: "bar",
                            data: values,
                            label: {
                                show: true,
                                position: "top",
                                color: textColor,
                                formatter: "{c}"
                            },
                            itemStyle: {
                                color: chartPalette[0]
                            }
                        }
                    ]
                };

            case "line":
                return {
                    color: getRandomPaletteFromChartPalette(chart.data.length),
                    xAxis: {
                        type: "category",
                        data: labels,
                        name: chart.xLabel,
                        axisLine: { lineStyle: { color: gridColor } },
                        axisLabel: { color: textColor }
                    },
                    yAxis: {
                        type: "value",
                        name: chart.yLabel,
                        axisLine: { lineStyle: { color: gridColor } },
                        axisLabel: { color: textColor }
                    },
                    series: [
                        {
                            type: "line",
                            data: values,
                            label: {
                                show: true,
                                position: "top",
                                color: textColor
                            },
                            itemStyle: {
                                color: (params) => chartPalette[params.dataIndex % chartPalette.length]
                            },
                            lineStyle: { width: 3 }
                        }
                    ]
                };

            case "pie":
                return {
                    color: getRandomPaletteFromChartPalette(chart.data.length),
                    tooltip: {
                        trigger: "item",
                        textStyle: { color: textColor },
                        formatter: params =>
                            `${params.name}: ${params.value.toFixed(1)} (${params.percent.toFixed(1)}%)`
                    },
                    series: [
                        {
                            type: "pie",
                            radius: "60%",
                            data: chart.data.map(el => ({
                                name: el.label,
                                value: el.value
                            })),
                            label: {
                                show: true,
                                color: textColor,
                                formatter: params =>
                                    `${params.name}\n${params.value.toFixed(0)} (${params.percent.toFixed(0)}%)`
                            },
                            labelLine: {
                                show: true,
                                lineStyle: { color: gridColor }
                            }
                        }
                    ]
                };

            case "treemap":
                return {
                    color: getRandomPaletteFromChartPalette(chart.data.length),
                    title: {
                        show: chart.title.show ?? true,
                        text: chart.title.text,
                        left: "center",
                        textStyle: { fontSize: 16, color: textColor }
                    },
                    tooltip: { formatter: "{b}: {c}", textStyle: { color: textColor } },
                    series: [
                        {
                            type: "treemap",
                            data: chart.data.map(el => ({
                                name: el.label,
                                value: el.value
                            })),
                            label: {
                                show: true,
                                color: textColor
                            },
                            itemStyle: {
                                gapWidth: 2,
                                color: (params) => chartPalette[params.dataIndex % chartPalette.length]
                            }
                        }
                    ]
                };

            case "map":
                const mapValues =
                    chart.data
                        ?.map(el => el.value)
                        .filter(v => typeof v === "number" && !isNaN(v)) ?? [];
                const maxVal = mapValues.length > 0 ? Math.max(...mapValues) : 0;
                return {
                    title: {
                        text: chart.title,
                        show: chart.title.show ?? true,
                        left: "center",
                        textStyle: { fontSize: 16, color: textColor }
                    },
                    tooltip: {
                        trigger: "item",
                        formatter: "{b}: {c}",
                        textStyle: { color: textColor }
                    },
                    visualMap: {
                        min: 0,
                        max: maxVal,
                        left: "left",
                        top: "bottom",
                        text: ["+", "-"],
                        textStyle: { color: textColor },
                        calculable: true,
                        inRange: {
                            color: [chartPalette[0], chartPalette[2], chartPalette[5]]
                        }
                    },
                    series: [
                        {
                            name: chart.seriesName || "",
                            type: "map",
                            map: chart.mapType || "world",
                            roam: true,
                            zoom: 3,
                            nameProperty: "Name",
                            data:
                                chart.data?.map(el => ({
                                    name: el.name === "Chhuk" ? "Chuuk" : el.name,
                                    value: Number(el.value) || 0,
                                    label: {
                                        show: true,
                                        formatter: "{c}",
                                        color: textColor,
                                        fontSize: 16,
                                        fontWeight: "bold",
                                        textBorderColor: isDark ? "#000" : "#fff",
                                        textBorderWidth: 2
                                    },
                                    emphasis: {
                                        label: {
                                            show: true,
                                            color: "#fff",
                                            fontWeight: "bold"
                                        }
                                    }
                                })) ?? []
                        }
                    ]
                };

            default:
                return {};
        }
    };



    const exportToPdf = () => {
        const instance = chartRef.current?.getEchartsInstance();
        if (!instance) return;

        const imgData = instance.getDataURL({
            type: "png",
            pixelRatio: 2,
            backgroundColor: "#fff"
        });

        const pdf = new jsPDF();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        const img = new Image();
        img.src = imgData;
        img.onload = () => {
            const imgWidth = img.width;
            const imgHeight = img.height;
            const ratio = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);

            const renderWidth = imgWidth * ratio;
            const renderHeight = imgHeight * ratio;

            const x = (pageWidth - renderWidth) / 2;
            const y = (pageHeight - renderHeight) / 2;

            pdf.addImage(imgData, "PNG", x, y, renderWidth, renderHeight);
            pdf.save(`chart-${chart.title.text}.pdf`);
            setAnchorEl(null);
        };
    };

    const exportToExcel = () => {
        const data = chart.data;
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Grafico");
        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array"
        });
        const blob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        saveAs(blob, `chart-${chart.title.text}-data.xlsx`);
        setAnchorEl(null);
    };



    return (
        <Box
            width={width}
            display="flex"
            flexDirection="column"
            alignItems="center"
            p={width === "100%" ? 2 : 1}
            gap={2}
        >
            {/* HEADER con titolo e controlli */}
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: `1px solid ${theme.palette.mode === 'light' ? theme.palette.newColors.gray : theme.palette.newColors.lightGray}`,
                    pb: 1,
                }}
            >
                {/* Titolo */}
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : theme.palette.newColors.lightGray,
                    }}
                >
                    <Trans>{chart?.title.text || ""}</Trans>
                </Typography>

                {/* Controlli a destra */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        backgroundColor: theme.palette.mode === 'light' ? theme.palette.newColors.lightGray : theme.palette.newColors.gray,
                        border: "1px solid #ddd",
                        borderRadius: 2,

                    }}
                >
                    {chart.type !== "map" && (
                        <TextField
                            select
                            size="small"
                            value={chart.type}
                            onChange={(event) => handleCharts(event, index)}
                            sx={{
                                "& .MuiInputBase-root": {
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'light' ? theme.palette.newColors.lightGray : theme.palette.newColors.gray,
                                    fontSize: 14,
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                },
                            }}
                        >
                            <MenuItem value="line">
                                <Trans>line</Trans>
                            </MenuItem>
                            <MenuItem value="bar">
                                <Trans>bar</Trans>
                            </MenuItem>
                            <MenuItem value="pie">
                                <Trans>pie</Trans>
                            </MenuItem>
                            <MenuItem value="treemap">
                                <Trans>treemap</Trans>
                            </MenuItem>
                        </TextField>
                    )}

                    {chart.type === "map" && (
                        <TextField
                            select
                            size="small"
                            value={selectedLocation}
                            onChange={(event) => setSelectedLocation(event.target.value)}
                            sx={{
                                "& .MuiInputBase-root": {
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'light' ? theme.palette.newColors.lightGray : theme.palette.newColors.gray,
                                    fontSize: 14,
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                },
                            }}
                        >
                            {locationsList.map((loc, k) => (
                                <MenuItem value={loc} key={k}>
                                    {loc}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}

                    <Tooltip title={_(msg`Download chart`)}>
                        <IconButton
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                            sx={{
                                backgroundColor: theme.palette.newColors.gray,
                                border: "1px solid #ddd",
                                borderRadius: 2,
                                width: 36,
                                height: 36,
                                "&:hover": {
                                    backgroundColor: theme.palette.newColors.gray,
                                },
                            }}
                        >
                            <SvgCustomIcon name={'download'} color={'white'}/>
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* POPUP per download */}
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                PaperProps={{
                    sx: {
                        p: 1,
                        borderRadius: 2,
                        boxShadow: 4,
                        minWidth: 200,
                    },
                }}
            >
                <Box
                    onClick={exportToExcel}
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 1,
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                    }}
                >
                    <Typography>Download Excel</Typography>
                    <Download sx={{ ml: 1 }} />
                </Box>
                <Box
                    onClick={exportToPdf}
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 1,
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                    }}
                >
                    <Typography>Download PDF</Typography>
                    <PictureAsPdf sx={{ ml: 1 }} />
                </Box>
            </Popover>

            {/* CHART */}
            <ReactECharts
                ref={chartRef}
                style={{ width: "100%", height: 500 + (chart.margin?.bottom ?? 0) }}
                option={getChartOption(chart)}
                notMerge={true}
            />
        </Box>

    );
});

export default EChart;
